/**
 * ChauffeurChatPage — D2-18: Chat côté chauffeur
 * Conversation avec le dispatch admin
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MessageSquare, Send, Loader2, CheckCircle } from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

export default function ChauffeurChatPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const endRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dispatch/pro/driver-chat/messages`, { headers: getHeaders() });
      setMessages(data.messages || []);
      setUnread(data.unread || 0);
      if (data.unread > 0) {
        await axios.post(`${API}/dispatch/pro/driver-chat/read`, {}, { headers: getHeaders() });
        setUnread(0);
      }
    } catch (err) { console.error('Chat error', err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMessages(); const iv = setInterval(fetchMessages, 10000); return () => clearInterval(iv); }, [fetchMessages]);
  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/dispatch/pro/driver-chat/messages`, { content: input.trim() }, { headers: getHeaders() });
      setMessages(prev => [...prev, data]);
      setInput('');
    } catch (err) { console.error('Send error', err); }
    setSending(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] bg-[#0a0a14]" data-testid="chauffeur-chat-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/5 shrink-0">
        <h2 className="text-sm font-semibold text-[#C9A24D] flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />Chat Dispatch
          {unread > 0 && <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-red-500/20 text-red-400">{unread}</span>}
        </h2>
        <p className="text-[10px] text-white/30 mt-0.5">Communication avec le dispatch</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="chat-messages">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#C9A24D]" /></div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-white/30">
            <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm">Aucun message</p>
            <p className="text-xs mt-1">Envoyez un message au dispatch</p>
          </div>
        ) : messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.sender_role === 'driver' ? 'justify-end' : 'justify-start'}`}
            data-testid={`chat-msg-${msg.id}`}>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${msg.sender_role === 'driver'
              ? 'bg-[#C9A24D]/20 border border-[#C9A24D]/30 rounded-br-sm'
              : 'bg-white/5 border border-white/10 rounded-bl-sm'}`}>
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`text-[10px] font-medium ${msg.sender_role === 'driver' ? 'text-[#C9A24D]' : 'text-white/60'}`}>
                  {msg.sender_role === 'driver' ? 'Vous' : msg.sender_name}
                </span>
                <span className="text-[9px] text-white/25">{formatTime(msg.created_at)}</span>
                {msg.read_at && msg.sender_role === 'driver' && <CheckCircle className="w-2.5 h-2.5 text-emerald-400/50" />}
              </div>
              <p className="text-xs text-white/80 whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-white/5 shrink-0" data-testid="chat-input-area">
        <div className="flex gap-2">
          <Input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Message au dispatch..."
            className="flex-1 bg-white/5 border-white/10 text-white text-sm rounded-xl"
            data-testid="chat-input" />
          <Button onClick={handleSend} disabled={!input.trim() || sending}
            className="bg-[#C9A24D] hover:bg-[#b8913f] text-black rounded-xl" size="sm" data-testid="chat-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
